import { useEffect, useState } from 'react'
import { useCallsInfinite } from '../api'
import type { CallRecord } from '../types'
import { formatDateTime, formatDuration } from '../lib/format'
import { getReasonDisplay } from '../config/callReasons'

const SEARCH_DEBOUNCE_MS = 300
const PAGE_SIZE = 10

type Variant = 'desktop' | 'mobile'

interface CallHistoryPanelProps {
  phoneId: string
  variant: Variant
}

function DirectionIcon({ direction }: { direction: string }) {
  if (direction === 'outgoing') {
    return (
      <svg
        className="w-4 h-4 text-blue-500 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-label="Исходящий"
      >
        <line x1="7" y1="17" x2="17" y2="7" />
        <polyline points="7 7 17 7 17 17" />
      </svg>
    )
  }
  return (
    <svg
      className="w-4 h-4 text-green-500 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label="Входящий"
    >
      <line x1="17" y1="7" x2="7" y2="17" />
      <polyline points="17 17 7 17 7 7" />
    </svg>
  )
}

function ReasonBadge({ call }: { call: CallRecord }) {
  const cfg = getReasonDisplay(call.reason, call.direction, call.duration_seconds)
  return (
    <span
      className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${cfg.color} ${cfg.bg}`}
    >
      {cfg.label}
    </span>
  )
}

function CallRow({ call }: { call: CallRecord }) {
  return (
    <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800/60 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
      <div className="flex items-center gap-2 mb-1">
        <DirectionIcon direction={call.direction} />
        <span className="font-mono text-sm text-gray-900 dark:text-gray-100 truncate flex-1">
          {call.number || '—'}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums shrink-0">
          {formatDuration(call.duration_seconds)}
        </span>
      </div>
      <div className="flex items-center justify-between pl-6">
        <ReasonBadge call={call} />
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {formatDateTime(call.ended_at)}
        </span>
      </div>
    </div>
  )
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

/**
 * Operator-side call history for a single phone. See plan §C8/C9 for
 * layout: desktop renders this in a fixed right `<aside>`, mobile
 * renders it full-width inside the `history` bottom-nav tab.
 *
 * Filtering is server-side: phone_id pinned, search debounced 300ms.
 * key_version scope is implicit — backend pins it to the current
 * operator's `users.key_version` (plan §C1).
 */
export function CallHistoryPanel({ phoneId, variant }: CallHistoryPanelProps) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)

  const query = useCallsInfinite({
    phoneId,
    search: debouncedSearch || undefined,
    pageSize: PAGE_SIZE,
  })

  const items = query.data?.pages.flatMap((p) => p.items) ?? []
  const containerCls =
    variant === 'desktop'
      ? 'flex flex-col w-full h-full bg-white dark:bg-gray-900'
      : 'flex flex-col w-full h-full bg-white dark:bg-gray-900'

  return (
    <div className={containerCls}>
      <div className="px-3 pt-3 pb-2 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
          История
        </h2>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по номеру"
          className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {query.isLoading ? (
          <div className="p-4 text-sm text-gray-500 text-center">Загрузка…</div>
        ) : query.isError ? (
          <div className="p-4 text-sm text-red-500 text-center">
            Не удалось загрузить историю
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-gray-400 dark:text-gray-500 text-center">
            История пуста
          </div>
        ) : (
          <>
            {items.map((c) => <CallRow key={c.id} call={c} />)}
            {query.hasNextPage && (
              <div className="p-3">
                <button
                  type="button"
                  onClick={() => query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                  className="w-full text-xs px-3 py-2 rounded-md border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-50 transition-colors"
                >
                  {query.isFetchingNextPage ? 'Загрузка…' : 'Загрузить ещё'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
